/**
 * Reset Password Page Component
 * Allows users to set a new password after verification
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle, X, Eye, EyeOff, Check, Circle, ArrowLeft } from 'lucide-react';
import customAuthService from '../../services/customAuthService';
import { validatePassword, validatePasswordConfirmation, checkPasswordStrength } from '../../utils/validation';
import { useTranslationNamespace } from '../../i18n/hooks/useTranslationNamespace';
import '../../styles/auth/CustomLoginPage.css';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslationNamespace('login');
  const email = location.state?.email || '';
  const code = location.state?.code || '';

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(null);

  // Redirect if no email or code provided
  useEffect(() => {
    if (!email || !code) {
      navigate('/forgot-password');
    }
  }, [email, code, navigate]);

  // Check password strength on change
  useEffect(() => {
    if (formData.newPassword) {
      const strength = checkPasswordStrength(formData.newPassword);
      setPasswordStrength(strength);
    } else {
      setPasswordStrength(null);
    }
  }, [formData.newPassword]);

  /**
   * Handle input change
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field error
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    if (error) {
      setError('');
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    // Validate password
    const passwordValidation = validatePassword(formData.newPassword, {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumber: true,
      requireSpecial: true,
    }, t);

    if (!passwordValidation.isValid) {
      setFieldErrors({ newPassword: passwordValidation.error });
      return;
    }

    // Validate password confirmation
    const confirmValidation = validatePasswordConfirmation(
      formData.newPassword,
      formData.confirmPassword,
      t
    );

    if (!confirmValidation.isValid) {
      setFieldErrors({ confirmPassword: confirmValidation.error });
      return;
    }

    setLoading(true);

    try {
      await customAuthService.resetPassword(email, code, formData.newPassword);

      // Show success message and redirect to login
      alert('Password reset successful! Please login with your new password.');
      navigate('/login');
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="custom-login-page">
      {/* Header */}
      <header className="custom-login-header">
        <Link to="/" className="custom-login-logo">
          <img
            src="/logo.svg"
            alt="Promotly Logo"
            className="custom-login-logo-img"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <span className="custom-login-logo-text">Promotly</span>
        </Link>
      </header>

      {/* Main Container */}
      <div className="custom-login-container">
        {/* Form Section (Centered) */}
        <div className="custom-login-form-section" style={{ gridColumn: '1 / -1' }}>
          <div className="custom-login-form-wrapper">
            {/* Back Button */}
            <Link
              to="/verify-code"
              state={{ email }}
              className="custom-login-link"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}
            >
              <ArrowLeft size={16} style={{ transform: document.documentElement.dir === 'rtl' ? 'rotate(180deg)' : 'none' }} />
              Back
            </Link>

            {/* Title */}
            <h1 className="custom-login-title">Reset Password</h1>
            <p className="custom-login-subtitle">
              Create a new password for <strong>{email}</strong>
            </p>

            {/* Error Alert */}
            {error && (
              <div className="custom-login-error-alert" role="alert">
                <AlertCircle className="custom-login-error-icon" size={18} />
                <span>{error}</span>
                <button
                  className="custom-login-error-dismiss"
                  onClick={() => setError('')}
                  aria-label="Dismiss error"
                >
                  <X size={18} />
                </button>
              </div>
            )}

            {/* Form */}
            <form className="custom-login-form" onSubmit={handleSubmit} noValidate>
              {/* New Password Field */}
              <div className="custom-login-form-group">
                <label htmlFor="newPassword" className="custom-login-label">
                  New Password
                </label>
                <div className="custom-login-password-wrapper">
                  <input
                    id="newPassword"
                    name="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    className={`custom-login-input ${fieldErrors.newPassword ? 'error' : ''}`}
                    placeholder="Enter new password"
                    value={formData.newPassword}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="new-password"
                    autoFocus
                    aria-invalid={!!fieldErrors.newPassword}
                    aria-describedby={fieldErrors.newPassword ? 'newPassword-error' : undefined}
                    required
                  />
                  <button
                    type="button"
                    className="custom-login-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {fieldErrors.newPassword && (
                  <span id="newPassword-error" className="custom-login-field-error" role="alert">
                    {fieldErrors.newPassword}
                  </span>
                )}
              </div>

              {/* Password Requirements Checklist */}
              {passwordStrength && (
                <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                  <p className="custom-login-label" style={{ marginBottom: '12px' }}>
                    Password Requirements:
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {passwordStrength.requirements.map((req) => (
                      <div
                        key={req.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          color: req.met ? '#10b981' : '#94a3b8',
                        }}
                      >
                        {req.met ? <Check size={16} /> : <Circle size={16} />}
                        <span>{req.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Confirm Password Field */}
              <div className="custom-login-form-group">
                <label htmlFor="confirmPassword" className="custom-login-label">
                  Confirm Password
                </label>
                <div className="custom-login-password-wrapper">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    className={`custom-login-input ${fieldErrors.confirmPassword ? 'error' : ''}`}
                    placeholder="Confirm new password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="new-password"
                    aria-invalid={!!fieldErrors.confirmPassword}
                    aria-describedby={fieldErrors.confirmPassword ? 'confirmPassword-error' : undefined}
                    required
                  />
                  <button
                    type="button"
                    className="custom-login-password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {fieldErrors.confirmPassword && (
                  <span id="confirmPassword-error" className="custom-login-field-error" role="alert">
                    {fieldErrors.confirmPassword}
                  </span>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="custom-login-button"
                disabled={loading || !formData.newPassword || !formData.confirmPassword}
                aria-busy={loading}
              >
                {loading ? (
                  <>
                    <span className="custom-login-spinner" aria-hidden="true"></span>
                    <span>Resetting...</span>
                  </>
                ) : (
                  <span>Reset Password</span>
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="custom-login-footer">
              <p style={{ marginTop: '16px', marginBottom: '0' }}>
                Remember your password?{' '}
                <Link to="/login" className="custom-login-link" style={{ marginInlineStart: '8px' }}>
                  Login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;